/**
 * Chart configuration constants
 * Centralized configuration for chart behavior and appearance
 */

export const chartConfig = {
  /**
   * Strike price label spacing
   */
  strikeLabels: {
    minSpacingPx: 80, // Minimum pixels between strike price labels
    bufferPercent: 0.1, // 10% buffer for visible range
    baseIntervalFallback: 25, // Fallback strike interval when detection fails
  },

  /**
   * Trade zone appearance
   */
  tradeZones: {
    opacity: {
      inactive: 0.04, // Background opacity when zone is not selected
      active: 0.08, // Background opacity when zone is selected
    },
    borderWidth: 6,
    borderRadius: 8,
  },

  /**
   * Laser animation settings
   */
  laser: {
    gradientSteps: 8, // Number of overlapping segments for smooth gradient
    baseSegmentLength: 0.08, // Base segment length as fraction of perimeter
    segmentOffset: 0.3, // Stagger multiplier for segments
    animation: {
      durationActive: '2.5s',
      durationInactive: '4s',
    },
    glow: {
      active: 24, // Glow radius in pixels when active
      inactive: 24, // Glow radius in pixels when inactive
    },
    opacity: {
      active: 0.5,
      inactive: 0.5,
    },
    strokeWidth: {
      active: 4,
      inactive: 3,
    },
  },

  /**
   * Strike price pill
   */
  strikePill: {
    width: 175,
    height: 36,
    borderRadius: 8,
    dragThreshold: 3, // Pixels to move before drag starts (mouse)
    touchDragThreshold: 5, // Pixels to move before drag starts (touch)
    fontSize: 13,
    fontWeight: 'bold' as const,
  },

  /**
   * Price indicators (ABOVE/BELOW labels on selected strike)
   */
  priceIndicators: {
    verticalOffset: 35, // Distance from strike line
    pillHeight: 35,
    pillPadding: 10,
    fontSize: {
      label: 14, // ABOVE/BELOW text
      price: 12, // Price value
      spread: 10, // Spread percentage
    },
    fontWeight: {
      label: 'bold' as const,
      price: '600' as const,
      spread: '500' as const,
    },
  },

  /**
   * Grid lines
   */
  gridLines: {
    opacity: {
      noStrikes: 0.03, // When no strike data available
      mainStrike: 0.15, // Regular main strike lines
      closestStrike: 0.4, // Highlighted closest strike (when none selected)
      subdivision: 0.05, // Filler lines between strikes
    },
    strokeDashArray: '2,2',
    glowStrokeWidth: 3,
    maxVisibleStrikesForSubdivision: 20, // Don't show subdivision lines if more strikes visible
    label: {
      height: 24,
      fontSize: 13,
      fontWeight: '500' as const,
      borderRadius: 7,
      background: 'rgba(0, 0, 0, 0.85)',
      border: 'rgba(255, 255, 255, 0.25)',
    },
    subdivisionLabel: {
      width: 44,
      height: 15,
      fontSize: 10,
      borderRadius: 4,
      background: 'rgba(0, 0, 0, 0.3)',
    },
  },

  /**
   * Price line visualization
   */
  priceLine: {
    strokeWidth: 2,
    shadowBlur: 1,
    currentPrice: {
      circleRadius: {
        inner: 4, // Solid core circle
        outer: 8, // Outer glow circle
      },
      opacity: {
        inner: 1.0,
        outer: 0.3,
      },
      pulseRadius: 4, // Expanding pulse ring
      shadowBlur: 3,
    },
  },

  /**
   * Position markers
   */
  positionMarkers: {
    strokeWidth: {
      selected: 5,
      normal: 3,
    },
    opacity: {
      selected: 1.0,
      normal: 0.75,
    },
    labelWidth: 70,
    clickThreshold: 16, // Pixels within which clicks register as position selection
    fontSize: {
      arrow: 14,
      pnl: 12,
    },
    fontWeight: 'bold' as const,
  },

  /**
   * Interaction thresholds
   */
  interaction: {
    snapThreshold: 15, // Pixels - snap to strike when dragging within this distance
    strikeSelectDistance: 30, // Pixels - clicking within this distance selects a strike
    strikeDeselectDistance: 30, // Pixels - clicking this far from strikes deselects
    positionClickThreshold: 16, // Pixels - clicking within this distance selects position
    debounce: {
      deselection: 200, // ms - prevent immediate reselection
      postDrag: 300, // ms - prevent deselection right after drag
    },
    zoom: {
      touchpadSensitivity: 0.03,
      mouseSensitivity: 0.1,
      shiftMultiplier: 2, // Multiply sensitivity when shift held
    },
  },

  /**
   * Right-side price labels (non-selected strikes)
   */
  rightPriceLabels: {
    rightEdge: 45, // X position from left
    verticalOffset: 10, // Offset from strike line
    fontSize: {
      price: 11,
      spread: 8,
    },
    fontWeight: {
      price: '500' as const,
      spread: 'normal' as const,
    },
  },

  /**
   * Time range indicator
   */
  timeIndicator: {
    fontSize: 11,
    yOffset: 8, // From bottom
  },
} as const;
